/* @flow */

import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_PLAYINGHISTORY, NETGEM_API_PLAYINGHISTORY_ITEM } from '../../libs/netgemLibrary/v8/types/PlayingHistory';
import type { KeyValuePair } from '../jsHelpers/types';

import { LoadableStatus } from '../loadable/loadable';
import Localizer from '../localization';
import { Messenger, MessengerEvents } from '../messenger';
import GameManager from './gameManager';

const FULL_HD_HEIGHT = 1080;

// Raw nicknames start with a GUID (without hyphens)
const NICKNAME_START_INDEX = 33;

const buildUrl = (urlStr: string, endpoint: string): URL => {
  const url = new URL(endpoint, urlStr);

  const baseParams = new URL(urlStr).searchParams;
  baseParams.forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  return url;
};

const buildSessionPayload = (gameAlias: string, profileId: string): KeyValuePair<string | number | boolean> => {
  const {
    screen: { height, width },
  } = window;

  return {
    alias: gameAlias,
    architecture: '',
    brand: 'Windows',
    client: 'mew-web',
    'client-version': '3.x.x',
    gamepad: true,
    language: Localizer.language,
    os: 'Windows',
    'os-version': '10',
    pinCodeCheck: '',
    platform: 'js',
    profile: profileId,
    resolution: height >= FULL_HD_HEIGHT ? '1080p' : '720p',
    'screen-height': height,
    'screen-width': width,
    touchscreen: false,
  };
};

const notifyError = (errorCode: number, errorMsg?: string): void => {
  const messages = GameManager.getErrorMessagesFromCode(errorCode);

  for (const { lang, value } of messages) {
    if (lang === Localizer.language) {
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, value);
      return;
    }
  }

  const key: string = typeof errorMsg === 'string' ? 'gaming.errors.global_with_message' : 'gaming.errors.global_without_message';

  Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize(key, { errorMsg: errorMsg ?? '' }));
};

const copyOrInitPlayingHistoryItem = (playingHistoryStatus: LoadableStatus, playingHistory: NETGEM_API_PLAYINGHISTORY, item: NETGEM_API_V8_FEED_ITEM): NETGEM_API_PLAYINGHISTORY_ITEM | null => {
  if (playingHistoryStatus !== LoadableStatus.Loaded) {
    return null;
  }

  const {
    selectedLocation: { channelId = '', id: locationId },
    selectedProgramId: itemId,
  } = item;

  const currentPlayingHistoryItem = playingHistory[itemId];

  if (currentPlayingHistoryItem === undefined) {
    return { channelId, locationId, profiles: {} };
  }

  return { ...currentPlayingHistoryItem, profiles: { ...currentPlayingHistoryItem.profiles } };
};

const extractNickname = (rawNickname: string): string => rawNickname.substring(NICKNAME_START_INDEX);

export { buildSessionPayload, buildUrl, copyOrInitPlayingHistoryItem, extractNickname, notifyError };
